<template>
  <a-modal
    :open="open"
    width="750px"
    :body-style="{ height: 'min(750px, 75vh)' }"
    :keyboard="false"
    centered
    :mask-closable="false"
    title=""
    @cancel="handleClose"
  >
    <section class="steps--container" v-if="!typeSequence">
      <TypeSequence :portfolioId="getCurrentPortfolio()" @selectedType="selectedType" />
    </section>

    <template v-else>
      <CustomStepper v-model:value="currentStep" :items="steps" readonly />

      <section class="steps--container">
        <keep-alive>
          <component
            ref="currentStepRef"
            :distributionType="typeSequence"
            :key="steps[currentStep]"
            :is="StepComponent[currentStep as keyof typeof StepComponent]"
            :portfolioId="getCurrentPortfolio()"
            @toggle-button="(disabled: boolean) => toggleDisabledButton(disabled)"
          />
        </keep-alive>
      </section>
    </template>

    <template #footer>
      <a-button @click="handleSecondaryAction" v-if="typeSequence">
        {{ secondaryButtonText }}
      </a-button>

      <a-tooltip>
        <template #title v-if="showTooltipButtonPrimary">
          Hay identificadores sin seleccionar o elimínalos para crear la secuencia.
        </template>
        <a-button
          v-if="typeSequence"
          type="primary"
          class="button_primary"
          :disabled="currentStep === 0 || (typeSequence == SequenceType.Multichannel && currentStep === 2) ? isButtonDisabled : false"
          :loading="loading"
          @click="handlePrimaryAction"
        >
          {{ primaryButtonText }}
        </a-button>
      </a-tooltip>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";

import { ResponseError } from "@/app/shared/utils/httpManager";
import { sequenceService } from "@/app/flows/sequences/services/Sequence.service";
import { CreateSequenceDto } from "@/@types/flows/sequences/dto";

import CustomStepper from "@/app/shared/components/organisms/CustomStepper.vue";
import TypeSequence from "@/app/flows/sequences/components/steps/TypeSequence.vue";
import ConfigurationStep from "@/app/flows/sequences/components/steps/ConfigurationStep.vue";
import ScheduleStep from "@/app/flows/sequences/components/steps/ScheduleStep.vue";
import IdentifiersStep from "@/app/flows/sequences/components/steps/IdentifiersStep.vue";
import UsersStep from "@/app/flows/sequences/components/steps/UsersStep.vue";
import { SequenceType, SequenceStep } from "@/app/flows/sequences/utils/enums";

const typeSequence = ref();

// const StepComponent = {
//   0: ConfigurationStep,
//   1: ScheduleStep,
//   2: UsersStep,
// } satisfies Record<number, unknown>;

const StepComponent = computed(() => {
  if (typeSequence.value == SequenceType.Multichannel) {
    return {
      0: ConfigurationStep,
      1: ScheduleStep,
      2: IdentifiersStep,
      // 3: UsersStep,
    } satisfies Record<number, unknown>;
  } else {
    return {
      0: ConfigurationStep,
      1: ScheduleStep,
      2: UsersStep,
    } satisfies Record<number, unknown>;
  }
});

// const steps = ["Configuración", "Horario", "Usuarios"];
const steps = computed(() => {
  if (typeSequence.value == SequenceType.Multichannel) {
    return [
      SequenceStep.Configuration,
      SequenceStep.Schedule,
      SequenceStep.Identifiers,
      // SequenceStep.Users,
    ];
  } else {
    return [SequenceStep.Configuration, SequenceStep.Schedule, SequenceStep.Users];
  }
});

const CANCEL_ACTION_TEXT = "Cancelar";
const BACK_STEP_ACTION_TEXT = "Volver";
const NEXT_STEP_ACTION_TEXT = "Continuar";
const CREATE_ACTION_TEXT = "Crear secuencia";
const OMITE_ACTION_TEXT = "Omitir por ahora";

defineProps<{ open: boolean }>();

const emit = defineEmits<{ "update:open": [open: boolean]; update: [] }>();

const currentStepRef =
  ref<InstanceType<typeof ConfigurationStep | typeof ScheduleStep | typeof UsersStep | typeof IdentifiersStep>>();
const currentStep = ref(0);
const secondaryButtonText = ref(CANCEL_ACTION_TEXT);
const primaryButtonText = ref(NEXT_STEP_ACTION_TEXT);
const isButtonDisabled = ref(true);

const handleClose = () => emit("update:open", false);

const toggleDisabledButton = (disabled: boolean) => {
  isButtonDisabled.value = disabled;
};

watch(currentStep, (stepValue: number) => {
  if (stepValue === 0) secondaryButtonText.value = CANCEL_ACTION_TEXT;
  if (stepValue > 0) secondaryButtonText.value = BACK_STEP_ACTION_TEXT;
  if (stepValue === steps.value.length - 1) primaryButtonText.value = CREATE_ACTION_TEXT;
  if (stepValue < steps.value.length - 1) primaryButtonText.value = NEXT_STEP_ACTION_TEXT;

  // valida step de identificadores valido
  nextTick(() => {
    if (typeSequence.value == SequenceType.Multichannel && stepValue === 2) {
      currentStepRef.value?.validateToggleButton();
    }
  });
});

const showTooltipButtonPrimary = computed(() => {
  return typeSequence.value == SequenceType.Multichannel && currentStep.value === 2 ? isButtonDisabled.value : false
});

const handleSecondaryAction = () => {
  if (currentStep.value > 0) {
    currentStep.value--;
    toggleDisabledButton(false);
    return;
  }

  handleClose();
};

let createSequenceDto: CreateSequenceDto;

const getCurrentPortfolio = () => createSequenceDto?.portfolio;

const getFormStateActual = computed(() => {
  return currentStepRef.value?.getFormState() || null;
});

watch(getFormStateActual, (value) => {
  if (currentStep.value === 1 && value?.schedules?.length == 0) {
    primaryButtonText.value = OMITE_ACTION_TEXT;
  }
});

const handlePrimaryAction = async () => {
  await currentStepRef.value?.formRef?.validate();

  createSequenceDto = { ...createSequenceDto, ...currentStepRef.value?.getFormState() };
  if (currentStep.value < steps.value.length - 1) {
    currentStep.value++;
    toggleDisabledButton(true);
    return;
  }

  handleRequest();
};

const router = useRouter();
const loading = ref(false);

const handleRequest = async () => {
  loading.value = true;

  try {
    const sequence = await sequenceService.create({
      ...createSequenceDto,
      sequence_type: typeSequence.value,
    });
    emit("update");
    router.push(`/flows/sequences/${sequence.id}`);
    handleClose();
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";

    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();

    message.error(errorMessage);
    console.error(error);
  }

  loading.value = false;
};

const selectedType = (type: number) => {
  typeSequence.value = type;
};
</script>

<style scoped lang="sass">
.steps
  &--container
    margin: 24px 60px
    height: calc(100% - 100px)

.button_primary
  margin-inline-start: 8px
</style>
